import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { isApiError } from '@/api/errors';
import { Badge } from '@/components/Badge';
import { Button, buttonClasses } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { formatParty, isLocationType } from '@/venue/venue';
import { Amount, CartLines, CartTotals, EmptyCart, Notice, Panel } from './parts';
import { RequestLines } from './RequestsPage';
import { TransferSheet } from './sheets';
import { useAssistance, useFloor, useReadyOrders, useSessionDetail, useStaffLive } from './queries';
import {
  describeStaffError,
  formatDuration,
  guestWord,
  locationText,
  sessionStatusLabel,
} from './wording';
import { SectionHeading } from '@/components/SectionHeading';
import { StickyActions } from '@/components/StickyActions';
import { cardClasses } from '@/components/Card';
import { cn } from '@/lib/cn';

export function SessionPage() {
  useStaffLive();
  const { sessionId = null } = useParams();
  const session = useSessionDetail(sessionId);
  const assistance = useAssistance();
  const floor = useFloor();
  const ready = useReadyOrders();
  const [moving, setMoving] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const back = (
    <Link
      to="/staff"
      className="inline-flex min-h-11 items-center gap-1 self-start text-sm font-semibold text-primary no-underline"
    >
      <Icon name="chevronLeft" size={18} />
      Floor
    </Link>
  );

  if (session.isPending) {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <LoadingRegion slowHint label="Loading the session">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-16" />
            <Skeleton className="h-32" />
          </div>
        </LoadingRegion>
      </div>
    );
  }

  if (session.isError && !session.data) {
    const gone = isApiError(session.error) && session.error.hasCode('SESSION_NOT_FOUND');
    return (
      <div className="flex flex-col gap-3">
        {back}
        {gone ? (
          <EmptyState
            icon="alert"
            title="Session not found"
            message={describeStaffError(session.error, 'load')}
          />
        ) : (
          <ErrorState error={session.error} onRetry={() => void session.refetch()} />
        )}
      </div>
    );
  }

  const data = session.data;
  if (!data) return null;

  const type = data.location.location_type;
  const where = locationText(type, data.location.label);
  const party = isLocationType(type)
    ? formatParty(type, data.party_display_name, data.guest_count)
    : data.party_display_name;
  const closed = data.status === 'CLOSED';
  const opened = Math.max(0, Math.floor((now - new Date(data.opened_at).getTime()) / 60_000));
  const requests = (assistance.data ?? []).filter((r) => r.session_id === data.session_id);
  const floorSession = floor.data
    ?.flatMap((l) => l.active_sessions)
    .find((s) => s.session_id === data.session_id);
  const hasReady = (ready.data ?? []).some((o) => o.session_id === data.session_id);
  const cart = data.draft_cart;

  return (
    <section aria-labelledby="session-title" className="flex min-h-full flex-col gap-4">
      {back}
      <header className="flex flex-col gap-1">
        {/* The badge is reserved in the title row, so a long guest name wraps under it instead of pushing it down. */}
        <div className="flex items-start justify-between gap-3">
          <h1
            id="session-title"
            className="min-w-0 flex-1 text-2xl leading-8 font-bold break-words text-ink"
          >
            {where}
          </h1>
          <Badge tone={closed ? 'neutral' : 'active'} className="mt-1.5 shrink-0">
            {sessionStatusLabel(data.status)}
          </Badge>
        </div>
        <p className="text-sm break-words text-muted">
          {guestWord(type)}: {party}
          {data.location.zone_name ? ` · ${data.location.zone_name}` : ''}
        </p>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted">
          <span>{formatDuration(opened)}</span>
          {floorSession?.has_pending_orders && <Badge tone="active">In the kitchen</Badge>}
          {hasReady && <Badge tone="success">Ready to serve</Badge>}
        </p>
      </header>

      {session.isError && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void session.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh this session. Showing the last details received.
        </Notice>
      )}
      {closed && <Notice tone="warn">This session is closed. Nothing more can be added.</Notice>}

      {floorSession && floorSession.bill_total > 0 && (
        <section
          aria-labelledby="bill-title"
          className={cn(
            cardClasses({ padding: 'none' }),
            'flex items-center justify-between gap-3 px-4 py-3',
          )}
        >
          <div className="min-w-0">
            <h2 id="bill-title" className="text-sm font-semibold text-muted">
              Bill total
            </h2>
            <p className="text-xs text-faint">
              For everything ordered so far. Payment is taken at the counter.
            </p>
          </div>
          <Amount
            minor={floorSession.bill_total}
            className="shrink-0 text-2xl leading-8 font-bold text-ink tabular-nums"
          />
        </section>
      )}

      {requests.length > 0 && (
        <section aria-labelledby="help-title" className="flex flex-col gap-2">
          <SectionHeading id="help-title">Help requests</SectionHeading>
          {/* The table, guest and zone are in the header above: only the requests themselves are listed. */}
          <RequestLines
            requests={requests}
            now={now}
            className={cn(cardClasses({ padding: 'none' }), 'px-4')}
          />
        </section>
      )}

      <section aria-labelledby="cart-title" className="flex flex-col gap-2">
        <SectionHeading
          id="cart-title"
          action={
            cart.items.length > 0 && (
              <span className="text-sm text-muted tabular-nums">
                {cart.total_quantity} {cart.total_quantity === 1 ? 'item' : 'items'}
              </span>
            )
          }
        >
          Customer&apos;s cart
        </SectionHeading>
        {cart.items.length === 0 ? (
          <EmptyCart />
        ) : (
          <Panel className="overflow-hidden">
            <CartLines cart={cart} locationType={type} />
            <CartTotals cart={cart} />
          </Panel>
        )}
        <p className="text-sm text-muted">
          The {guestWord(type).toLowerCase()} reviews and places this order from their own phone.
        </p>
      </section>

      {!closed && (
        // The bar sits after the cart in the flow, so the last line and the totals scroll clear of it.
        <StickyActions tabBar="phone" className="mt-2 md:-mx-6 md:px-6">
          <div role="group" aria-label="Session actions" className="flex items-center gap-2">
            <Link
              to={`/staff/menu?session=${data.session_id}`}
              className={buttonClasses({ variant: 'primary', size: 'lg' }) + ' flex-1'}
            >
              <Icon name="plus" size={18} />
              Add to customer&apos;s cart
            </Link>
            <Button
              variant="secondary"
              size="lg"
              disabled={!floor.data}
              onClick={() => setMoving(true)}
            >
              Move
            </Button>
          </div>
        </StickyActions>
      )}

      {moving && floor.data && (
        <TransferSheet
          sessionId={data.session_id}
          fromLocationId={data.location.location_id}
          from={where}
          party={data.party_display_name}
          locations={floor.data}
          onClose={() => setMoving(false)}
          onMoved={() => setMoving(false)}
        />
      )}
    </section>
  );
}
